/**
 * The panel's own login (contract 0.4.0, ADR 0012): who is signed in, username and
 * password, the 6 digit code or a recovery code, logout. The session itself is an HttpOnly
 * cookie the page never sees; this client only reads the answers. Two implementations: the
 * HTTP one below and the mock (./mock/auth.ts, development and the static preview only).
 */

import {
  API_BASE_PATH,
  type AuthSession,
  apiErrorSchema,
  authSessionSchema,
  HEADERS,
  loginResponseSchema,
  loginTotpResponseSchema,
  logoutResponseSchema,
} from '@twin/contracts';

export type { AuthSession };

export type LoginStep =
  | { kind: 'totp'; challenge: string; expiresAt: string }
  | { kind: 'done'; session: AuthSession; recoveryCodesLeft: null };

export interface SignedIn {
  session: AuthSession;
  /** Set when a recovery code was used: how many are left. */
  recoveryCodesLeft: number | null;
}

export type SecondFactor = { code: string } | { recoveryCode: string };

export interface AuthApi {
  session(): Promise<AuthSession>;
  login(username: string, password: string): Promise<LoginStep>;
  verify(challenge: string, factor: SecondFactor): Promise<SignedIn>;
  logout(): Promise<void>;
}

/**
 * A refused login step. code: invalid_credentials, login_locked (retryAfterSeconds set),
 * login_expired, totp_not_enrolled, csrf, rate_limited, or "network" when no answer came.
 */
export class AuthError extends Error {
  readonly code: string;
  readonly status: number;
  readonly retryAfterSeconds: number | null;
  constructor(code: string, message: string, status = 0, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Short Turkish text for a refused step; the lockout says how long. */
export function authErrorText(error: unknown): string {
  if (!(error instanceof AuthError)) return 'Beklenmeyen bir hata oldu. Tekrar dene.';
  switch (error.code) {
    case 'invalid_credentials':
      return 'Kullanıcı adı, parola veya kod hatalı.';
    case 'login_locked': {
      const minutes = Math.max(1, Math.ceil((error.retryAfterSeconds ?? 900) / 60));
      return `Çok fazla hatalı deneme. Giriş ${minutes} dakika kilitli; sonra tekrar dene.`;
    }
    case 'login_expired':
      return 'Kod adımının süresi doldu. Kullanıcı adı ve parolayla baştan başla.';
    case 'totp_not_enrolled':
      return 'İki adımlı doğrulama kurulmamış. Sunucuda "twin-auth.sh enroll-totp" çalıştır.';
    case 'rate_limited':
      return 'Çok fazla istek. Biraz bekleyip tekrar dene.';
    case 'network':
      return 'Sunucuya ulaşılamadı. Bağlantını kontrol et.';
    default:
      return error.message.trim() === '' ? 'Giriş yapılamadı.' : error.message;
  }
}

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export function createHttpAuthApi(options: { basePath?: string; fetch?: FetchLike } = {}): AuthApi {
  const basePath = options.basePath ?? API_BASE_PATH;
  const doFetch: FetchLike = options.fetch ?? ((input, init) => fetch(input, init));

  async function send(method: 'GET' | 'POST', path: string, body?: unknown): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (method === 'POST') headers[HEADERS.twinRequest] = '1';
    if (body !== undefined) headers['content-type'] = 'application/json; charset=utf-8';
    let response: Response;
    try {
      const init: RequestInit = { method, headers, credentials: 'same-origin', cache: 'no-store' };
      if (body !== undefined) init.body = JSON.stringify(body);
      response = await doFetch(`${basePath}${path}`, init);
    } catch {
      throw new AuthError('network', 'istek gitmedi');
    }
    const text = await response.text();
    let json: unknown = null;
    try {
      json = text === '' ? null : JSON.parse(text);
    } catch {
      json = null;
    }
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(json);
      const retryHeader = Number(response.headers.get(HEADERS.retryAfter));
      const retry = Number.isFinite(retryHeader) && retryHeader > 0 ? retryHeader : null;
      if (parsed.success)
        throw new AuthError(parsed.data.error.code, parsed.data.error.message, response.status, retry);
      throw new AuthError(
        response.status >= 500 ? 'internal' : 'unknown',
        `Sunucu hatası (${response.status})`,
        response.status,
      );
    }
    return json;
  }

  function checked<T>(
    schema: { safeParse(value: unknown): { success: true; data: T } | { success: false } },
    value: unknown,
  ): T {
    const parsed = schema.safeParse(value);
    if (!parsed.success) throw new AuthError('unknown', 'Yanıt beklenen biçimde değil');
    return parsed.data;
  }

  return {
    async session() {
      return checked(authSessionSchema, await send('GET', '/auth/session'));
    },
    async login(username, password) {
      const answer = checked(loginResponseSchema, await send('POST', '/auth/login', { username, password }));
      return answer.next === 'totp'
        ? { kind: 'totp', challenge: answer.challenge, expiresAt: answer.expiresAt }
        : { kind: 'done', session: answer.session, recoveryCodesLeft: null };
    },
    async verify(challenge, factor) {
      return checked(loginTotpResponseSchema, await send('POST', '/auth/login/totp', { challenge, ...factor }));
    },
    async logout() {
      checked(logoutResponseSchema, await send('POST', '/auth/logout'));
    },
  };
}
